/**
 * /mfa/enrol — first-time TOTP enrolment.
 *
 * Step 1 hits POST /v1/auth/mfa/enrol; the server returns a
 * provisioning URI which we render as a QR code via qrcode.react.
 * Step 2 verifies the user has scanned and configured their app
 * by submitting a code; on success the server returns ten plaintext
 * recovery codes which we surface for the user to record.
 */

import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../auth/AuthContext";
import { authApi, type AuthApiError } from "../../auth/api";

type Step = "loading" | "scan" | "confirmed";

export function MfaEnrol() {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const [step, setStep] = useState<Step>("loading");
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (principal === null) {
      return;
    }
    let cancelled = false;
    void authApi
      .mfaEnrol()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setProvisioningUri(result.provisioning_uri);
        setSecret(result.secret);
        setStep("scan");
      })
      .catch((exc: AuthApiError) => {
        if (cancelled) {
          return;
        }
        setError(exc.message ?? "Could not start MFA enrolment.");
      });
    return () => {
      cancelled = true;
    };
  }, [principal]);

  async function onConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await authApi.mfaEnrolConfirm(code);
      setRecoveryCodes(result.recovery_codes);
      setStep("confirmed");
    } catch (exc) {
      const apiError = exc as AuthApiError;
      setError(apiError.message ?? "Code did not match.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-title">Set up two-factor authentication</h1>

        {step === "loading" && (
          <p className="auth-subtle">Generating your enrolment...</p>
        )}

        {step === "scan" && provisioningUri !== null && (
          <>
            <p className="auth-subtle">
              Scan this QR code with your authenticator app, then enter
              the six-digit code it shows to confirm.
            </p>
            <div className="auth-qr">
              {/* level="H" gives ~30% Reed-Solomon recovery so a marginal
                  scan does not silently converge on a different valid
                  codeword (the failure mode behind "scan worked but the
                  code never verifies"). marginSize=4 is the QR spec's
                  recommended quiet zone; the deprecated includeMargin
                  prop is replaced. Larger size gives more pixels per
                  module on dense URIs. */}
              <QRCodeSVG
                value={provisioningUri}
                size={240}
                level="H"
                marginSize={4}
              />
            </div>
            {secret !== null && (
              <div className="auth-secret-block">
                <span className="auth-label">Setup key</span>
                <div className="auth-secret-row">
                  <code className="auth-secret-value">{secret}</code>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      void navigator.clipboard.writeText(secret);
                    }}
                  >
                    Copy
                  </button>
                </div>
                <p className="auth-subtle">
                  If scanning fails, add the entry manually with this
                  key; the result is identical.
                </p>
              </div>
            )}
            <form onSubmit={onConfirm} noValidate>
              <label className="auth-field">
                <span className="auth-label">Verification code</span>
                <input
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              {error !== null && (
                <div className="auth-error" role="alert">
                  {error}
                </div>
              )}
              <button type="submit" className="auth-primary" disabled={busy}>
                {busy ? "Confirming..." : "Confirm"}
              </button>
            </form>
          </>
        )}

        {step === "confirmed" && (
          <>
            <p className="auth-subtle">
              MFA is active. Save these recovery codes somewhere safe;
              each works exactly once if you lose access to your
              authenticator app.
            </p>
            <ul className="auth-recovery-codes">
              {recoveryCodes.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="auth-primary"
              onClick={() => navigate("/", { replace: true })}
            >
              Continue to workspace
            </button>
          </>
        )}

        <div className="auth-links">
          <Link to="/">Skip for now</Link>
        </div>
      </div>
    </div>
  );
}
